import { api } from "../../api";

// ===== EXISTING FUNCTIONS =====

export async function fetchBusinessByEmail(email) {
  try {
    const response = await api.get(`/business/businesses?email=${email}`);
    console.log('📧 Business data received:', response.data);
    
    // Ensure we return business data with proper structure
    if (response.data && Array.isArray(response.data)) {
      return response.data.map(business => ({
        business_id: business.business_id,
        business_name: business.business_name || business.name || `Business ${business.business_id}`,
        ...business // Include any other fields
      }));
    }
    
    return response.data || [];
  } catch (error) {
    console.error('Error fetching business data:', error);
    return [];
  }
}

export const aggregateRatingsData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Create a map to store counts for each rating value
  const ratingCounts = {};
  
  // Count occurrences of each rating
  data.forEach(item => {
    // Assuming your value is in item.value or similar property
    const rating = parseFloat(item.value || item.rating || item);
    
    if (!isNaN(rating)) {
      // Round to nearest 0.5 to handle potential floating point issues
      const roundedRating = Math.round(rating * 2) / 2;
      
      if (!ratingCounts[roundedRating]) {
        ratingCounts[roundedRating] = 0;
      }
      ratingCounts[roundedRating]++;
    }
  });
  
  // Convert the map to the array format expected by your charts
  return Object.entries(ratingCounts).map(([rating, count]) => ({
    name: rating, // Use the rating as the name/category
    value: count  // Use the count as the value
  })).sort((a, b) => parseFloat(b.name) - parseFloat(a.name)); // Sort by rating descending
};

/**
 * Aggregates yes/no responses
 * @param {Array} data - Original chart data array
 * @returns {Array} Aggregated data with counts for "yes" and "no" responses
 */
export const aggregateYesNoData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Initialize counters
  let yesCount = 0;
  let noCount = 0;
  
  // Count yes/no responses
  data.forEach(item => {
    // Assuming your response is in item.value or similar property
    const response = (item.value || item.response || item).toString().toLowerCase();
    
    if (response === 'yes') {
      yesCount++;
    } else if (response === 'no') {
      noCount++;
    }
  });
  
  // Return the aggregated data in the format expected by your charts
  return [
    { name: 'Yes', value: yesCount },
    { name: 'No', value: noCount }
  ];
};

//v2 data import - fetch data for the device id and deck id
export async function fetchData(deviceId, deckId) {
    // console.log('Fetching data for device ID (fetch data):', deviceId)
    // console.log('Fetching data for deck ID:', deckId)
    // try {
      const response1 = await api.get(`/analytics/question_1?device_id=${deviceId}&deck_id=${deckId}`);
  
      const response2 = await api.get(`/analytics/question_2?device_id=${deviceId}&deck_id=${deckId}`);
  
      const response3 = await api.get(`/analytics/question_3?device_idd=${deviceId}&deck_id=${deckId}`);
      const r1type = response1.data.results[0].response_type
      const r2type = response2.data.results[0].response_type
      const r3type = response3.data.results[0].response_type
      let scaleGraph = 1
      const graph1 = response1.data.results.map(item => { 
        if (r1type.toLowerCase() === 'rating') {
          // console.log('The scale question deck pos is: 1')
          return item.rating !== undefined ? item.rating : null;
        } else {
          return item.yes_no !== undefined ? item.yes_no : null;
        }
      }).filter(value => value !== null).sort((a, b) => b - a);
      
      // Choose the correct property for question2 based on responseType
      const graph2 = response2.data.results.map(item => {
        if (r2type.toLowerCase() === 'rating') {
          scaleGraph = 2
          // console.log('The scale question deck pos is: 2')
          return item.rating;
        } else {
          return item.yes_no;
        }
      });
      
      // Choose the correct property for question3 based on responseType
      const graph3 = response3.data.results.map(item => {
        if (r3type.toLowerCase() === 'rating') {
          scaleGraph = 3
          // Fixed syntax error in original
          return item.rating;
        } else {
          return item.yes_no;
        }
      });
      let graph4 = response1.data.results.map(item => ({ feedback_time: item.feedback_time, value: item.rating}))

      if (scaleGraph === 2){
        // console.log('The scale question deck pos is: 2')
        graph4 = response2.data.map(item => ({ feedback_time: item.feedback_time, value: item.rating}))

      } else if (scaleGraph === 3){
        // console.log('The scale question deck pos is: 3')
        graph4 = response3.data.map(item => ({ feedback_time: item.feedback_time, value: item.rating}))
      }
      
      return [
        graph1, graph2, graph3, graph4
      ]
}

//v3 data import - fetch data by all devices from the store_id
export async function fetchDataBySiteId(siteId, businessId = null) {
  // console.log('Fetching data for device ID (fetch data):', siteId)
  // try {

    // Build query parameters
    const buildQueryParams = (siteId, businessId) => {
      const params = new URLSearchParams({ site_id: siteId });
      if (siteId === 'all' && businessId) {
        params.append('business_id', businessId);
        console.log('🌐 Fetching ALL SITES data for business:', businessId);
      } else {
        console.log('🏢 Fetching data for single site:', siteId);
      }
      return params.toString();
    };

    const queryParams = buildQueryParams(siteId, businessId);
    console.log('📡 API Query params:', queryParams);

    const response1 = await api.get(`/analytics/v2/question_1?${queryParams}`);
    console.log('✅ Question 1 data received:', response1.data.results?.length || 0, 'records');
    console.log('📊 Q1 First record:', response1.data.results?.[0]);

    const response2 = await api.get(`/analytics/v2/question_2?${queryParams}`);
    console.log('✅ Question 2 data received:', response2.data.results?.length || 0, 'records');

    const response3 = await api.get(`/analytics/v2/question_3?${queryParams}`);
    console.log('✅ Question 3 data received:', response3.data.results?.length || 0, 'records');
    const r1type = response1.data.results[0]?.response_type
    const r2type = response2.data.results[0]?.response_type
    const r3type = response3.data.results[0]?.response_type
    console.log('📝 Response types detected:', { r1type, r2type, r3type });
    let scaleGraph = 1
    const graph1 = response1.data.results.map(item => {
      let value = null;
      if (r1type?.toLowerCase() === 'rating') {
        value = item.rating !== undefined ? item.rating : null;
      } else {
        value = item.yes_no !== undefined ? item.yes_no : null;
      }
      return { feedback_time: item.feedback_time, value, device_id: item.device_id };
    }).filter(item => item.value !== null).sort((a, b) => b.value - a.value);

    console.log('📈 Graph1 processed:', {
      originalCount: response1.data.results?.length || 0,
      afterFilter: graph1.length,
      firstItem: graph1[0],
      sampleItems: graph1.slice(0, 3)
    });

    // Choose the correct property for question2 based on responseType
    const graph2 = response2.data.results.map(item => {
      let value = null;
      if (r2type?.toLowerCase() === 'rating') {
        scaleGraph = 2;
        value = item.rating;
      } else {
        value = item.yes_no;
      }
      return { feedback_time: item.feedback_time, value, device_id: item.device_id };
    });

    // Choose the correct property for question3 based on responseType
    const graph3 = response3.data.results.map(item => {
      let value = null;
      if (r3type?.toLowerCase() === 'rating') {
        scaleGraph = 3;
        value = item.rating;
      } else {
        value = item.yes_no;
      }
      return { feedback_time: item.feedback_time, value, device_id: item.device_id };
    });

    let graph4 = response1.data.results.map(item => ({ feedback_time: item.feedback_time, value: item.rating, device_id: item.device_id }))

    if (scaleGraph === 2){
      // console.log('The scale question deck pos is: 2')
      graph4 = response2.data.results.map(item => ({ feedback_time: item.feedback_time, value: item.rating, device_id: item.device_id}))

    } else if (scaleGraph === 3){
      // console.log('The scale question deck pos is: 3')
      graph4 = response3.data.results.map(item => ({ feedback_time: item.feedback_time, value: item.rating, device_id: item.device_id}))
    }

    console.log('🎯 Data being returned from fetchDataBySiteId:', {
      graph1Length: graph1.length,
      graph2Length: graph2.length,
      graph3Length: graph3.length,
      graph4Length: graph4.length
    });

    return [
      graph1, graph2, graph3, graph4
    ]
}

export async function fetchDeckByStoreId(store_id){
  // console.log('FetchCurrentDeck with id', siteId)
  try {
    const response = await api.get(`/analytics/deck_id?store_id=${store_id}`);
    // console.log('deckId:', response.data[0]?.deck_id)
    return response.data[0]?.deck_id;
  } catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
}

//Function to fetch the deck slide titles and slide positions
export async function fetchSlidesByDeckId(deckId){
  try {
    const response = await api.get(`/analytics/slides?deck_id=${deckId}`);
    return response.data;
  }
  catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
}

// export function returnSortedAndAccumulatedNonPeakData(data, period){
export function returnSortedAndAccumulatedNonPeakData(data, period) {
  // console.log('First few items of data:', data.slice(0, 3)); // For debugging
  // console.log('data:', data)
  const datetimeDataPairs = data.map(item => {
    if (!item.feedback_time) {
      return null;
    }
    
    let feedback_time;
    switch (period) {
      case 'today':
      case 'yesterday':
        feedback_time = item.feedback_time.split('T')[1].split(':')[0]; // Only keep the hour part
        break;
      default:
        feedback_time = item.feedback_time.split('T')[0]; // Only keep the date part
        break;
    }
    
    // Determine value based on response_type
    let value;
    if (item.value !== undefined ) {
      value = item.value;
    } else {
      if (item.response_type && item.response_type.toLowerCase() === 'rating') {
        value = item.rating;
      } else {
        value = item.yes_no;
      }
    }
    
    return {
      feedback_time: feedback_time,
      value: value
    };
  }).filter(item => item !== null && item.value !== null);

  // console.log('datetimeDataPairs:', datetimeDataPairs.slice(0, 3)); // For debugging
  const accumulatedData = datetimeDataPairs.reduce((acc, curr) => {
    if (!acc[curr.feedback_time]) {
      acc[curr.feedback_time] = { yesCount: 0, noCount: 0 };
    }
    
    // Check response value and increment appropriate counter
    // console.log('curr.value:', curr.value)
    if (parseFloat(curr.value) >= 3) {
      acc[curr.feedback_time].yesCount += 1;
    } else {
      acc[curr.feedback_time].noCount += 1;
    }
    
    return acc;
  }, {});
  const sortedData = Object.entries(accumulatedData).sort((a, b) => a[0].localeCompare(b[0]));

  return sortedData;
}

//Graph 4 - Toggle for dates.
export const filterDataByDateRange = (data, range) => {
  if (!data || data.length === 0) return [];
  
  const now = new Date();
  let startDate, endDate;

  switch (range) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      break;
      
    case 'yesterday':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
      break;
      
    case 'week':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
      endDate = now;
      break;
      
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0, 0);
      endDate = now;
      break;
      
    case 'year':
      startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 0, 0, 0, 0);
      endDate = now;
      break;
      
    default:
      return data.sort((a, b) => new Date(a.feedback_time) - new Date(b.feedback_time));
  }

  return data
    .filter(item => {
      const itemDate = new Date(item.feedback_time);
      return itemDate >= startDate && itemDate <= endDate;
    })
    .sort((a, b) => new Date(a.feedback_time) - new Date(b.feedback_time));
};

export async function fetchSiteNameByStoreId(store_id) {
  try {
    const response = await api.get(`/analytics/store_names?store_id=${store_id}`);
    if (response.status !== 200) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const site = response.data;
    return site;
  } catch (error) {
    console.error('There was a problem with the get operation: ', error);
  }
}

// UPDATED: Primary function now uses StoresData API
export async function fetchStoreIdsByBusinessId(business_id) {
  try {
    console.log('🔄 Attempting to fetch stores using StoresData API for business:', business_id);
    
    // Try StoresData API first
    try {
      const response = await api.get(`/store-data/stores-with-availability/${business_id}`);
      if (response.status === 200 && response.data) {
        console.log('✅ Successfully fetched stores from StoresData API');
        // Convert StoresData format to original format for compatibility
        return response.data.map(store => ({
          store_id: store.store_id,
          name: store.store_name,
          business_id: store.business_id
        }));
      }
    } catch (storesDataError) {
      console.log('⚠️ StoresData API unavailable, falling back to original endpoint');
    }
    
    // Fallback to original endpoint
    const response = await api.get(`/analytics/store_ids?business_id=${business_id}`);
    if (response.status !== 200) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const site = response.data;
    return site;
  } catch (error) {
    console.error('There was a problem with the get operation: ', error);
    return []; // Return empty array instead of undefined
  }
}

export function getDataByPeriod(data, range, startDate, periodLength) {
  if (range === 'today' || range === 'yesterday') {
    // handle 'today' and 'yesterday' range here
    return data.reduce((acc, item) => {
      const date = new Date(item.feedback_time);
      const period = date.getHours(); // get the hour of the day (0-23)
      if (!acc[period]) {
          acc[period] = { yescount: 0, nocount: 0 };
      }
      // if (item.value === '5' || item.value === '4' || item.value === '3') {
      //     acc[period].yescount += 1;
      // } else if (item.value === '1' || item.value === '2') {
      //     acc[period].nocount += 1;
      // }
      if (parseFloat(item.value) >= 3) {
        acc[period].yescount += 1;
    } else {
        acc[period].nocount += 1;
    }
    return acc;
  }, {});
  } else {
    return data.reduce((acc, item) => {
      const periodStart = Math.floor((new Date(item.feedback_time).getTime() - startDate.getTime()) / periodLength) * periodLength + startDate.getTime();
      const periodDate = new Date(periodStart);
      const period = `${periodDate.getFullYear()}-${periodDate.getMonth() + 1}-${periodDate.getDate()}`;
      if (!acc[period]) {
          acc[period] = { yescount: 0, nocount: 0 };
      }
      if (parseFloat(item.value) >= 3) {
          acc[period].yescount += 1;
      } else {
          acc[period].nocount += 1;
      }
      return acc;
  }, {});
}
}

export const calculateBestPeriods = (data, range) => {
  const now = new Date();
  let startDate;
  let periodLength;
  let sortedData;
  switch (range) {
      case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          periodLength = 3 * 60 * 60 * 1000; // 3 hour in milliseconds
          break;
      case 'yesterday':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          periodLength = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
          break;
      case 'week':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          periodLength = 24 * 60 * 60 * 1000; // 1 day in milliseconds
          break;
      case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          periodLength = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds
          break;
      case 'year':
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          periodLength = 30 * 24 * 60 * 60 * 1000; // 1 month in milliseconds
          break;
      default:
          // Sort the data by date
          sortedData = data.sort((a, b) => {
              // Check if the objects exist before accessing their properties
              if (a && b) {
                  return new Date(a.feedback_time) - new Date(b.feedback_time);
              } else {
                  return 0;
              }
          });          
          // Check if the first data object exists before accessing its properties
          // console.log('sortedData:', sortedData)
          if (sortedData[0]) {
              // Set the start date to the date of the first data point
              startDate = new Date(sortedData[0].feedback_time);
          } else {
              // Set a default start date if the first data object doesn't exist
              startDate = new Date();
          }

          // Set the period length to the entire time range
          periodLength = now.getTime() - startDate.getTime(); // The entire time range
  }
  // Define and initialize lastDate
  const lastDate = new Date(Math.max.apply(null, data.map(item => new Date(item.feedback_time))));
  const firstDate = new Date(Math.min.apply(null, data.map(item => new Date(item.feedback_time))));
  // Filter the data based on the start date
  data = data.filter(item => new Date(item.feedback_time) >= startDate);

  // Group the data by period

  const dataByPeriod = getDataByPeriod(data, range, startDate, periodLength);

  // Calculate the best periods
  let mostHappyPeriod = { startDate: null, endDate: null, difference: -100 };
  let leastHappyPeriod = { startDate: null, endDate: null, difference: -100 };

  for (const period in dataByPeriod) {
    const { yescount, nocount } = dataByPeriod[period];
    const yesDifference = yescount - nocount;
    // console.log('yesDifference:', yesDifference)
    const noDifference = nocount - yescount;
    // console.log('noDifference:', noDifference)
    if (range === 'today' || range === 'yesterday') {
      let interimPeriod;
      if (period.length === 1) {
        interimPeriod = `0${period}`;
      }else{
        interimPeriod = period;
      }

      if (yesDifference > mostHappyPeriod.difference) {
        mostHappyPeriod = { 
          startDate: interimPeriod, 
          endDate: ((parseInt(interimPeriod, 10) + 1).toString().length === 1 ? '0' + (parseInt(interimPeriod, 10) + 1).toString() : (parseInt(interimPeriod, 10) + 1).toString()), 
          difference: yesDifference  // <-- CORRECTED
        };
      }
      if (noDifference > leastHappyPeriod.difference) {
        leastHappyPeriod = { 
          startDate: interimPeriod, 
          endDate: ((parseInt(interimPeriod, 10) + 1).toString().length === 1 ? '0' + (parseInt(interimPeriod, 10) + 1).toString() : (parseInt(interimPeriod, 10) + 1).toString()), 
          difference: noDifference 
      };
      }
    } else {
      
      const periodStartDate = new Date(period); 
      const periodEndDate = new Date(periodStartDate.getTime() + periodLength);
      if (lastDate >= periodEndDate) {
        if(firstDate <= periodStartDate) {
          if (yesDifference > mostHappyPeriod.difference) {
            mostHappyPeriod = { startDate: periodStartDate, endDate: periodEndDate, difference: yesDifference };
          }
          if (noDifference > leastHappyPeriod.difference) {
            leastHappyPeriod = { startDate: periodStartDate, endDate: periodEndDate, difference: noDifference };
          }
        }
      }
    }
  }
  console.log('mostHappyPeriod:', mostHappyPeriod);
  console.log('leastHappyPeriod:', leastHappyPeriod);

  const isOverlapping = 
    (mostHappyPeriod.startDate <= leastHappyPeriod.endDate && 
     mostHappyPeriod.endDate >= leastHappyPeriod.startDate);
  console.log('isOverlapping:', isOverlapping);
  if (isOverlapping) {
    if (leastHappyPeriod.startDate >= mostHappyPeriod.startDate && 
        leastHappyPeriod.startDate <= mostHappyPeriod.endDate) {
      leastHappyPeriod.startDate = mostHappyPeriod.endDate;
    }
    // If unhappy period ends during happy period, move it to before
    else if (leastHappyPeriod.endDate >= mostHappyPeriod.startDate && 
            leastHappyPeriod.endDate <= mostHappyPeriod.endDate) {
      leastHappyPeriod.endDate = mostHappyPeriod.startDate;
    }
    // If happy period is completely inside unhappy period, split the unhappy period
    else if (mostHappyPeriod.startDate >= leastHappyPeriod.startDate && 
            mostHappyPeriod.endDate <= leastHappyPeriod.endDate) {
      // In this case, we'll just shorten the unhappy period to end before the happy period
      leastHappyPeriod.endDate = mostHappyPeriod.startDate;
    }
  }
  // Return the best periods as arrays of objects
  return [
    { happyPeriodStart: mostHappyPeriod.startDate, happyPeriodEnd: mostHappyPeriod.endDate },
    { unhappyPeriodStart: leastHappyPeriod.startDate, unhappyPeriodEnd: leastHappyPeriod.endDate }
  ];
};

// ===== UPDATED SMART LOADING FUNCTIONS FOR STORESDATA INTEGRATION =====

/**
 * NEW: Uses StoresData API for instant data availability checking
 * @param {string} siteId - The site ID to check
 * @param {number} days - Number of days to look back (default: 30)
 * @returns {Promise<{siteId: string, hasData: boolean, priority: number}>}
 */
const quickDataCheck = async (siteId, days = 30) => {
  try {
    // Skip StoresData check since the endpoint doesn't exist
    // Use the original fallback method
    const response = await api.get(`/analytics/quick-check?site_id=${siteId}&days=${days}`);
    
    return {
      siteId,
      hasData: response.data.has_data,
      priority: response.data.count,
      recentCount: response.data.recent_count,
      lastActivity: response.data.last_activity,
      activityLevel: response.data.activity_level
    };
  } catch (error) {
    console.warn(`Quick check failed for site ${siteId}:`, error);
    return {
      siteId,
      hasData: false,
      priority: 0,
      recentCount: 0,
      lastActivity: null,
      activityLevel: 'none'
    };
  }
};
/**
 * UPDATED: Enhanced to use StoresData first, fallback to parallel checking
 * @param {string[]} siteIds - Array of site IDs to check
 * @param {number} minResponses - Minimum responses to consider a site "active"
 * @param {Function} progressCallback - Optional callback for progress updates
 * @returns {Promise<string|null>} - Best site ID or null if none found
 */
export const findBestSiteWithData = async (siteIds, minResponses = 5, progressCallback = null) => {
  if (!siteIds || siteIds.length === 0) {
    return null;
  }

  console.log(`🔍 Scanning ${siteIds.length} sites for data using StoresData...`);
  
  // Try to get pre-computed data for all sites at once
  try {
    const businessStoresResponse = await api.get(`/stores-with-availability/${siteIds[0].split('_')[0] || 'unknown'}`);
    if (businessStoresResponse.data && businessStoresResponse.data.length > 0) {
      console.log('✅ Using StoresData for instant site selection');
      
      // Filter to only the sites we're interested in
      const relevantSites = businessStoresResponse.data.filter(store => 
        siteIds.includes(store.store_id)
      );
      
      // Find best site based on StoresData
      const sitesWithData = relevantSites
        .filter(site => 
          (site.has_data_today || site.has_data_this_week) && 
          (site.responses_this_week || 0) >= minResponses
        )
        .sort((a, b) => {
          // Sort by: data today > quality score > responses today
          if (a.has_data_today !== b.has_data_today) {
            return b.has_data_today - a.has_data_today;
          }
          if (a.computed_quality_score !== b.computed_quality_score) {
            return (b.computed_quality_score || 0) - (a.computed_quality_score || 0);
          }
          return (b.responses_today || 0) - (a.responses_today || 0);
        });

      if (progressCallback) {
        progressCallback(siteIds.length, siteIds.length); // Mark as complete
      }

      if (sitesWithData.length > 0) {
        const bestSite = sitesWithData[0];
        console.log(`✅ StoresData found best site: ${bestSite.store_id} (${bestSite.responses_today || 0} responses today)`);
        return bestSite.store_id;
      }
    }
  } catch (error) {
    console.log('StoresData not available, falling back to parallel checking...');
  }
  
  // Fallback to original parallel checking method
  const dataChecks = await Promise.allSettled(
    siteIds.map(async (siteId, index) => {
      try {
        const result = await Promise.race([
          quickDataCheck(siteId),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 5000)
          )
        ]);
        
        if (progressCallback) {
          progressCallback(index + 1, siteIds.length);
        }
        
        return result;
      } catch (error) {
        if (progressCallback) {
          progressCallback(index + 1, siteIds.length);
        }
        throw error;
      }
    })
  );

  // Process results and filter successful checks
  const sitesWithData = dataChecks
    .filter(result => result.status === 'fulfilled')
    .map(result => result.value)
    .filter(site => site.hasData && site.priority >= minResponses)
    .sort((a, b) => {
      // Sort by priority (response count) and recency
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      // If same priority, prefer more recent activity
      return new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0);
    });

  if (sitesWithData.length > 0) {
    const bestSite = sitesWithData[0];
    console.log(`✅ Found best site: ${bestSite.siteId} (${bestSite.priority} responses, ${bestSite.activityLevel} activity)`);
    return bestSite.siteId;
  }

  // Fallback: return first site if no sites have enough data
  console.log(`⚠️ No sites with sufficient data found, falling back to first site`);
  return siteIds[0];
};

/**
 * UPDATED: Get pre-filtered sites using StoresData API
 * @param {string} businessId - Business ID
 * @param {number} minResponses - Minimum responses threshold
 * @returns {Promise<object|null>} - Sites data or null
 */
export const fetchSitesWithData = async (businessId, minResponses = 5) => {
  try {
    // Use StoresData API
    const response = await api.get(`/stores-with-availability/${businessId}`);
    
    if (response.data && Array.isArray(response.data)) {
      // Filter sites that meet minimum response criteria
      const qualifiedSites = response.data.filter(store => 
        (store.responses_this_week || 0) >= minResponses ||
        store.has_data_today ||
        (store.computed_quality_score || 0) > 0.5
      );
      
      return {
        sites: qualifiedSites.map(store => store.store_id),
        details: qualifiedSites.map(store => ({
          site_id: store.store_id,
          site_name: store.store_name,
          activity_level: store.has_data_today ? 'high' : 
                         store.has_data_this_week ? 'medium' : 'low',
          recent_responses: store.responses_today || 0,
          total_responses: store.responses_this_week || 0,
          quality_score: store.computed_quality_score || 0,
          has_data_today: store.has_data_today,
          has_data_this_week: store.has_data_this_week
        })),
        recommendations: {
          best_site: qualifiedSites[0]?.store_id,
          reason: qualifiedSites[0]?.has_data_today ? 'recent_activity' : 'best_available'
        }
      };
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to fetch sites using StoresData API:', error);
    
    // Fallback to original analytics endpoint if StoresData fails
    try {
      const response = await api.get(`/analytics/sites/with-data?business_id=${businessId}&min_responses=${minResponses}`);
      return {
        sites: response.data.sites || [],
        details: response.data.site_details || [],
        recommendations: response.data.recommendations || {}
      };
    } catch (fallbackError) {
      console.warn('Both StoresData and analytics endpoints failed:', fallbackError);
      return null;
    }
  }
};

/**
 * UPDATED: Smart site initialization with StoresData integration
 * @param {string} businessId - Business ID  
 * @param {string[]} allSiteIds - All available site IDs
 * @param {number} minResponses - Minimum responses threshold
 * @param {Function} progressCallback - Optional callback for progress updates
 * @returns {Promise<{bestSiteId: string, sitesWithData: string[], details: object}>}
 */
export const smartSiteInitialization = async (businessId, allSiteIds, minResponses = 5, progressCallback = null) => {
  console.log('🚀 Starting smart site initialization with StoresData...');
  
  try {
    // Strategy 1: Use StoresData API for instant results
    const storesDataResult = await fetchSitesWithData(businessId, minResponses);
    
    if (storesDataResult && storesDataResult.sites && storesDataResult.sites.length > 0) {
      console.log(`✅ StoresData provided ${storesDataResult.sites.length} sites with data`);
      
      if (progressCallback) {
        progressCallback(allSiteIds.length, allSiteIds.length); // Mark as complete
      }
      
      // Extract site pairings from the detailed results
      const sitePairings = {};
      if (storesDataResult.details && storesDataResult.details.length > 0) {
        storesDataResult.details.forEach(siteDetail => {
          sitePairings[siteDetail.site_id] = siteDetail.site_name || `Site ${siteDetail.site_id}`;
        });
      }
      
      // Get recommended site from StoresData
      let bestSiteId = storesDataResult.recommendations?.best_site;
      
      // If no recommendation, use the first site with data
      if (!bestSiteId && storesDataResult.sites.length > 0) {
        bestSiteId = storesDataResult.sites[0];
      }
      
      return {
        bestSiteId,
        sitesWithData: storesDataResult.sites,
        details: storesDataResult.details,
        sitePairings: sitePairings,
        strategy: 'storesdata_api'
      };
    }
  } catch (error) {
    console.log('StoresData initialization failed, using fallback methods...');
  }
  
  // Strategy 2: Fallback to original backend endpoint
  try {
    const backendResult = await fetchSitesWithData(businessId, minResponses);
    
    if (backendResult && backendResult.sites && backendResult.sites.length > 0) {
      console.log(`✅ Analytics backend provided ${backendResult.sites.length} sites with data`);
      
      if (progressCallback) {
        progressCallback(allSiteIds.length, allSiteIds.length);
      }
      
      const sitePairings = {};
      if (backendResult.details && backendResult.details.length > 0) {
        backendResult.details.forEach(siteDetail => {
          sitePairings[siteDetail.site_id] = siteDetail.site_name || `Site ${siteDetail.site_id}`;
        });
      }
      
      return {
        bestSiteId: backendResult.sites[0],
        sitesWithData: backendResult.sites,
        details: backendResult.details,
        sitePairings: sitePairings,
        strategy: 'analytics_backend'
      };
    }
  } catch (error) {
    console.log('Analytics backend pre-filtering unavailable, using parallel checking...');
  }
  
  // Strategy 3: Fallback to parallel checking
  const bestSiteId = await findBestSiteWithData(allSiteIds, minResponses, progressCallback);
  
  return {
    bestSiteId,
    sitesWithData: bestSiteId ? [bestSiteId] : [],
    details: [],
    strategy: 'parallel_check'
  };
};

/**
 * UPDATED: Enhanced with StoresData integration
 * @param {string} business_id - Business ID
 * @param {boolean} onlyWithData - If true, only return stores with recent data
 * @param {number} minResponses - Minimum responses threshold
 * @returns {Promise<Array>} - Array of store objects with activity info
 */
export const fetchStoreIdsWithActivityInfo = async (business_id, onlyWithData = false, minResponses = 5) => {
  try {
    if (onlyWithData) {
      // Use StoresData for smart filtering
      const smartResult = await fetchSitesWithData(business_id, minResponses);
      if (smartResult && smartResult.details) {
        return smartResult.details.map(site => ({
          store_id: site.site_id,
          site_name: site.site_name,
          has_data: true,
          activity_level: site.activity_level,
          recent_responses: site.recent_responses,
          total_responses: site.total_responses
        }));
      }
    }
    
    // Use StoresData API to get all stores with their availability info
    try {
      const response = await api.get(`/stores-with-availability/${business_id}`);
      if (response.status === 200 && response.data) {
        return response.data.map(store => ({
          store_id: store.store_id,
          site_name: store.store_name,
          has_data: store.has_recent_data,
          activity_level: store.has_data_today ? 'high' : 
                         store.has_data_this_week ? 'medium' : 'low',
          recent_responses: store.responses_today,
          total_responses: store.responses_this_week
        }));
      }
    } catch (storesDataError) {
      console.log('StoresData API unavailable, using fallback...');
    }
    
    // Fallback to original method
    const response = await api.get(`/analytics/store_ids?business_id=${business_id}`);
    if (response.status !== 200) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Return in consistent format
    return response.data.map(store => ({
      store_id: store.store_id,
      site_name: null, // Will need to be fetched separately
      has_data: null,  // Unknown without checking
      activity_level: null,
      recent_responses: null,
      total_responses: null
    }));
    
  } catch (error) {
    console.error('There was a problem with the get operation: ', error);
    return [];
  }
};

/**
 * UPDATED: Enhanced fetchDataBySiteId with StoresData pre-check
 * @param {string} siteId - Site ID to fetch data for (or 'all' for all sites)
 * @param {string} deckId - Deck ID
 * @param {boolean} skipIfEmpty - If true, return null for sites with no data
 * @param {string} businessId - Business ID (required when siteId is 'all')
 * @returns {Promise<Array|null>} - Chart data arrays or null if no data
 */
export const fetchDataBySiteIdSmart = async (siteId, deckId, skipIfEmpty = false, businessId = null) => {
  try {
    // If skipIfEmpty is true, you could add a quick check here
    // but for now, just proceed with normal data fetching

    // Use the working fetchDataBySiteId function, passing businessId
    const result = await fetchDataBySiteId(siteId, businessId);

    console.log('🔍 fetchDataBySiteIdSmart result:', {
      siteId,
      businessId,
      isArray: Array.isArray(result),
      length: Array.isArray(result) ? result.length : 'N/A',
      graph1Items: Array.isArray(result) && result[0] ? result[0].length : 0,
      graph2Items: Array.isArray(result) && result[1] ? result[1].length : 0,
      graph3Items: Array.isArray(result) && result[2] ? result[2].length : 0,
      graph4Items: Array.isArray(result) && result[3] ? result[3].length : 0
    });

    // Ensure we return the expected array format
    if (Array.isArray(result) && result.length >= 4) {
      return result;
    } else {
      console.warn('fetchDataBySiteId returned unexpected format:', result);
      return [[], [], [], []]; // Return empty arrays in expected format
    }

  } catch (error) {
    console.error(`Error fetching data for site ${siteId}:`, error);
    if (skipIfEmpty) {
      return null;
    }
    // Return empty arrays to maintain compatibility
    return [[], [], [], []];
  }
};

/**
 * Bulk check multiple sites for data availability using StoresData
 * @param {string[]} siteIds - Array of site IDs to check
 * @param {number} days - Days to look back for data
 * @returns {Promise<Array>} - Array of site status objects
 */
export const bulkSiteCheck = async (siteIds, days = 30) => {
  try {
    // Try StoresData bulk check first
    const response = await api.post('/bulk-site-check', {
      site_ids: siteIds,
      days: days
    });
    
    return response.data.sites || [];
  } catch (error) {
    console.error('Bulk site check failed, using individual checks:', error);
    // Fallback to individual checks
    return await Promise.allSettled(
      siteIds.map(siteId => quickDataCheck(siteId, days))
    ).then(results => 
      results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value)
    );
  }
};

/**
 * Get detailed activity summary for a specific site using StoresData
 * @param {string} siteId - Site ID to get summary for
 * @returns {Promise<object>} - Detailed site activity data
 */
export const getSiteSummary = async (siteId) => {
  try {
    // Try StoresData API first
    const response = await api.get(`/site-availability/${siteId}`);
    if (response.data) {
      return {
        site_id: siteId,
        has_data: response.data.has_recent_data,
        responses_today: response.data.responses_today,
        responses_this_week: response.data.responses_this_week,
        quality_score: response.data.computed_quality_score,
        last_activity: response.data.last_response_date,
        data_freshness: response.data.data_freshness
      };
    }
  } catch (error) {
    console.log('StoresData unavailable, trying analytics endpoint...');
  }
  
  // Fallback to original analytics endpoint
  try {
    const response = await api.get(`/analytics/site-summary?site_id=${siteId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get site summary for ${siteId}:`, error);
    return {
      site_id: siteId,
      has_data: false,
      message: 'Failed to retrieve site summary'
    };
  }
};

/**
 * Cache for site data checks to avoid repeated API calls
 */
const siteDataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Cached version of quick data check
 * @param {string} siteId - Site ID to check
 * @param {number} days - Days to look back
 * @returns {Promise<object>} - Cached or fresh site data
 */
export const quickDataCheckCached = async (siteId, days = 30) => {
  const cacheKey = `${siteId}-${days}`;
  const cached = siteDataCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  const freshData = await quickDataCheck(siteId, days);
  siteDataCache.set(cacheKey, {
    data: freshData,
    timestamp: Date.now()
  });
  
  return freshData;
};

/**
 * Clear the site data cache (useful when switching businesses)
 */
export const clearSiteDataCache = () => {
  siteDataCache.clear();
  console.log('🗑️ Site data cache cleared');
};

/**
 * Utility function to validate and sanitize site data
 * @param {Array} data - Raw site data array
 * @returns {Array} - Validated and cleaned data
 */
export const validateSiteData = (data) => {
  if (!Array.isArray(data)) {
    console.warn('Site data is not an array:', typeof data);
    return [];
  }
  
  return data.filter(item => {
    // Basic validation
    if (!item || typeof item !== 'object') {
      return false;
    }
    
    // Must have either a feedback_time or be a summary object
    if (!item.feedback_time && !item.value && !item.name) {
      return false;
    }
    
    return true;
  });
};

/**
 * Enhanced error handling wrapper for data fetching functions
 * @param {Function} fetchFunction - Function to execute
 * @param {string} context - Context description for logging
 * @param {*} fallbackValue - Value to return on error
 * @returns {Promise<*>} - Result or fallback value
 */
export const withErrorHandling = async (fetchFunction, context = 'data fetch', fallbackValue = []) => {
  try {
    const result = await fetchFunction();
    return result;
  } catch (error) {
    console.error(`Error during ${context}:`, error);
    
    // Check if it's a network error
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('fetch')) {
      console.log(`🔄 Network error during ${context}, may retry automatically`);
    }
    
    return fallbackValue;
  }
};

/**
 * Preload data for multiple sites in background
 * @param {string[]} siteIds - Array of site IDs to preload
 * @param {number} limit - Maximum number of concurrent requests
 * @returns {Promise<Map>} - Map of siteId to preloaded data
 */
export const preloadSiteData = async (siteIds, limit = 3) => {
  const preloadedData = new Map();
  
  // Process sites in batches to avoid overwhelming the server
  for (let i = 0; i < siteIds.length; i += limit) {
    const batch = siteIds.slice(i, i + limit);
    
    const batchPromises = batch.map(async (siteId) => {
      try {
        const data = await fetchDataBySiteIdSmart(siteId, true); // Skip if empty
        if (data) {
          preloadedData.set(siteId, data);
          console.log(`📦 Preloaded data for site ${siteId}`);
        }
      } catch (error) {
        console.warn(`Failed to preload data for site ${siteId}:`, error);
      }
    });
    
    await Promise.allSettled(batchPromises);
    
    // Small delay between batches
    if (i + limit < siteIds.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }
  
  console.log(`✅ Preloading complete: ${preloadedData.size}/${siteIds.length} sites`);
  return preloadedData;
};

/**
 * Get user's preferred sites based on previous selections (could be enhanced with actual user preferences)
 * @param {string} userId - User identifier
 * @param {string[]} availableSites - Available site IDs
 * @returns {Promise<string[]>} - Ordered array of preferred site IDs
 */
export const getUserPreferredSites = async (userId, availableSites) => {
  try {
    const recentSites = localStorage.getItem(`recent_sites_${userId}`);
    if (recentSites) {
      const parsed = JSON.parse(recentSites);
      const orderedSites = [];
      
      // Add recent sites first (if they're still available)
      parsed.forEach(siteId => {
        if (availableSites.includes(siteId)) {
          orderedSites.push(siteId);
        }
      });
      
      // Add remaining sites
      availableSites.forEach(siteId => {
        if (!orderedSites.includes(siteId)) {
          orderedSites.push(siteId);
        }
      });
      
      return orderedSites;
    }
    
    return availableSites;
  } catch (error) {
    console.warn('Error getting user preferred sites:', error);
    return availableSites;
  }
};

/**
 * Track user site selection for future preference learning
 * @param {string} userId - User identifier
 * @param {string} siteId - Selected site ID
 */
export const trackSiteSelection = (userId, siteId) => {
  try {
    const key = `recent_sites_${userId}`;
    const existing = localStorage.getItem(key);
    let recentSites = existing ? JSON.parse(existing) : [];
    
    // Remove if already exists
    recentSites = recentSites.filter(id => id !== siteId);
    
    // Add to front
    recentSites.unshift(siteId);
    
    // Keep only last 10
    recentSites = recentSites.slice(0, 10);
    
    localStorage.setItem(key, JSON.stringify(recentSites));
  } catch (error) {
    console.warn('Error tracking site selection:', error);
  }
};

// Export existing functions with their original names for backward compatibility
export {
  fetchDataBySiteId as fetchDataBySiteIdOriginal,
  fetchStoreIdsByBusinessId as fetchStoreIdsByBusinessIdOriginal
};